import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, Trophy, Target, TrendingUp, ArrowUpCircle, ArrowDownCircle, 
  AlertCircle, Search, ChevronDown, ChevronUp, Plus, Briefcase, 
  ArrowRight, Lightbulb, Download, FileSpreadsheet, Zap, 
  GraduationCap, Palmtree, Home, Star, Rocket, DollarSign, Heart, BookOpen,
  Grid3X3, List, BarChart3, Filter, Eye, Clock, Users, Calendar,
  TrendingDown, Percent, CheckCircle, XCircle, Play, BookMarked, Cog, Map,
  Settings, PlayCircle, FileText, Compass, User
} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { loadUserProgress } from './progressManager';

// Composant KPI amélioré avec données réelles
const KPICard = ({ icon: Icon, title, value, change, color, onClick, isActive, subtitle, details }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const numericValue = parseInt(value.toString().replace(/[^0-9]/g, '')) || 0;
    if (numericValue > 0) {
      const timer = setTimeout(() => {
        let current = 0;
        const increment = numericValue / 20;
        const counter = setInterval(() => {
          current += increment;
          if (current >= numericValue) {
            setAnimatedValue(numericValue);
            clearInterval(counter);
          } else {
            setAnimatedValue(Math.floor(current));
          }
        }, 50);
        return () => clearInterval(counter);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [value]);

  const displayValue = () => {
    if (typeof value === 'string') {
      if (value.includes('%')) return `${animatedValue}%`;
      if (value.includes('/')) return value; // Garde le format fractionné
      return value;
    }
    return animatedValue;
  };

  return (
    <div 
      className={`bg-white rounded-xl shadow-sm p-6 border-l-4 h-full transition-all duration-300 hover:shadow-lg ${
        onClick ? 'cursor-pointer' : ''
      } ${isActive ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
      style={{ borderLeftColor: color }}
      onClick={onClick}
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-gray-600 text-sm font-medium">{title}</h3>
          <p className="text-2xl font-bold mt-1 transition-all duration-500">
            {displayValue()}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="rounded-lg p-3 transition-all duration-300 group-hover:scale-110" 
             style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6 transition-all duration-300" style={{ color: color }} />
        </div>
      </div>
      
      {change !== undefined && (
        <div className="flex items-center gap-1 mt-2">
          {change >= 0 ? (
            <ArrowUpCircle className="w-4 h-4 text-green-500" />
          ) : (
            <ArrowDownCircle className="w-4 h-4 text-red-500" />
          )}
          <span className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change >= 0 ? '+' : ''}{change}% vs période précédente
          </span>
        </div>
      )}

      {showDetails && details && (
        <div className="mt-3 p-2 bg-gray-50 rounded-lg border transition-all duration-300">
          <div className="text-xs text-gray-600 space-y-1">
            {details.map((detail, index) => (
              <div key={index} className="flex justify-between">
                <span>{detail.label}:</span>
                <span className="font-medium">{detail.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Composant carte d'objectif
const ObjectiveCard = ({ objective, onClick }) => {
  const navigate = useNavigate();
  
  const phases = {
    'ambition': { color: 'from-purple-400 to-purple-600' }, 
    'stratégie': { color: 'from-blue-400 to-blue-600' },
    'action': { color: 'from-green-400 to-green-600' }
  };

  const phase = objective.phase || 'ambition';
  const phaseColor = phases[phase]?.color || 'from-gray-400 to-gray-600';
  const progress = objective.performance || 0;

  return (
    <div 
      className="bg-white rounded-lg shadow-sm hover:shadow border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 relative"
      onClick={() => onClick(objective)}
    >
      <div className={`h-1 w-full bg-gradient-to-r ${phaseColor}`} />
      
      <div className="p-4 space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="text-gray-800 mb-2 pr-3 flex-grow font-medium text-base line-clamp-2">
            {objective.name}
          </h3>
          
          <div className="flex-shrink-0 flex flex-col items-end">
            <span className="text-sm font-medium text-gray-600">{progress}%</span>
            <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
              <div
                className={`h-full bg-gradient-to-r transition-all duration-500 ${phaseColor}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <div className="flex items-center">
            <Star className="w-3.5 h-3.5 mr-1 text-gray-400" />
            {objective.keyConceptsCount || 0}
          </div>
          <div className="flex items-center">
            <Zap className="w-3.5 h-3.5 mr-1 text-gray-400" />
            {objective.completedActionsCount || 0}/{objective.actionsCount || 0}
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-3">
          {/* Bouton Stratégie - disponible pour toutes les phases */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/Recommandations/${objective.id}`, { 
                state: { competence: objective, objective: objective }
              });
            }}
            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-sm bg-indigo-50 px-3 py-1 rounded-lg"
          >
            <Map size={12} />
            Stratégie <ArrowRight size={14} />
          </button>

          {phase === 'stratégie' && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/Recommandations/${objective.id}`, { 
                  state: { competence: objective, objective: objective }
                });
              }}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm bg-blue-50 px-3 py-1 rounded-lg"
            >
              Recommandations <ArrowRight size={14} />
            </button>
          )}
          
          {(phase === 'action' || phase === 'stratégie') && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/actionPlan/${objective.id}`, { 
                  state: { competence: objective, objective: objective }
                });
              }}
              className="text-green-600 hover:text-green-800 flex items-center gap-1 text-sm bg-green-50 px-3 py-1 rounded-lg"
            >
              Plan d'action <ArrowRight size={14} />
            </button>
          )}
          
          {phase === 'action' && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/KnowledgeBase/${objective.id}`, { 
                  state: { competence: objective, objective: objective }
                });
              }}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm bg-blue-50 px-3 py-1 rounded-lg"
            >
              Ressources <ArrowRight size={14} />
            </button>
          )}
          
          {phase === 'ambition' && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/formulation-objectif`, { 
                  state: { editObjective: objective }
                });
              }}
              className="text-purple-600 hover:text-purple-800 flex items-center gap-1 text-sm bg-purple-50 px-3 py-1 rounded-lg"
            >
              Développer <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant carte de domaine
const DomainCard = ({ domain, objectives, isExpanded, toggleExpand, handleObjectiveClick }) => {
  const getDomainColor = (domainName) => {
    switch(domainName) {
      case 'Professionnel': return { 
        bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', gradient: 'from-purple-400 to-purple-600' 
      };
      case 'Personnel': return { 
        bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', gradient: 'from-green-400 to-green-600' 
      };
      case 'Educatif': return { 
        bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', gradient: 'from-blue-400 to-blue-600' 
      };
      case 'Business': return { 
        bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', gradient: 'from-indigo-400 to-indigo-600' 
      };
      case 'Activités ludique et créative': return { 
        bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', gradient: 'from-green-400 to-green-600' 
      };
      case 'Développement entrepreneurial': return { 
        bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', gradient: 'from-orange-400 to-orange-600' 
      };
      case 'Formation et apprentissage': return { 
        bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', gradient: 'from-indigo-400 to-indigo-600' 
      };
      case 'Évolution de carrière': return { 
        bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', gradient: 'from-violet-400 to-violet-600' 
      };
      case 'Santé et bien-être': return { 
        bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', gradient: 'from-emerald-400 to-emerald-600' 
      };
      case 'Développement personnel': return { 
        bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', gradient: 'from-teal-400 to-teal-600' 
      };
      case 'Loisirs et voyages': return { 
        bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', gradient: 'from-pink-400 to-pink-600' 
      };      
      case 'Gestion immobilière': return { 
        bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', gradient: 'from-amber-400 to-amber-600' 
      };
      case 'Finance et Banque': return { 
        bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', gradient: 'from-emerald-500 to-teal-600' 
      };
      case 'Activités sportives': return { 
        bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', gradient: 'from-red-400 to-orange-500' 
      };
      default: return { 
        bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', gradient: 'from-gray-400 to-gray-600' 
      };
    }
  };

  const completedObjectives = objectives.filter(obj => obj.performance >= 75).length;
  const completionPercentage = objectives.length > 0 
    ? Math.round((completedObjectives / objectives.length) * 100) 
    : 0;
  
  const colors = getDomainColor(domain);

  const getDomainIcon = () => {
    switch(domain) {
      case 'Professionnel':
        return <Briefcase className="w-7 h-7 text-purple-600" />;
      case 'Personnel':
        return <Target className="w-7 h-7 text-green-600" />;
      case 'Educatif':
        return <GraduationCap className="w-7 h-7 text-blue-600" />;
      case 'Business':
        return <FileSpreadsheet className="w-7 h-7 text-indigo-600" />;
      case 'Activités ludique et créative':
        return <Lightbulb className="w-7 h-7 text-green-600" />;
      case 'Développement entrepreneurial':
        return <Rocket className="w-7 h-7 text-orange-600" />;
      case 'Formation et apprentissage':
        return <BookOpen className="w-7 h-7 text-indigo-600" />;
      case 'Évolution de carrière':
        return <TrendingUp className="w-7 h-7 text-violet-600" />;
      case 'Santé et bien-être':
        return <Heart className="w-7 h-7 text-emerald-600" />;
      case 'Développement personnel':
        return <User className="w-7 h-7 text-teal-600" />;
      case 'Loisirs et voyages':
        return <Palmtree className="w-7 h-7 text-pink-600" />;        
      case 'Gestion immobilière':
        return <Home className="w-7 h-7 text-amber-600" />;
      case 'Finance et Banque':
        return <DollarSign className="w-7 h-7 text-emerald-600" />;
      case 'Activités sportives':
        return <Zap className="w-7 h-7 text-red-600" />;
      default:
        return <Target className="w-7 h-7 text-gray-600" />;
    }
  };
 
  return (
    <div className={`rounded-xl shadow-sm border ${colors.border} overflow-hidden transition-all duration-300 hover:shadow-md cursor-pointer`}>
      <div 
        className={`${colors.bg} transition-all`}
        onClick={() => toggleExpand(domain)}
      >
        <div className="flex items-center p-4">
          <div className="mr-4">
            {getDomainIcon()}
          </div>
          
          <div className="flex-grow">
            <h3 className={`text-lg font-semibold ${colors.text}`}>
              {domain}
            </h3>
            <div className="flex items-center text-sm text-gray-600 mt-1">
              <div className="flex items-center mr-4">
                <span>{objectives.length}</span>
                <span className="ml-1 text-gray-500">objectifs</span>
              </div>
              <div className="flex items-center">
                <span>{completedObjectives}</span>
                <span className="ml-1 text-gray-500">achevés</span>
              </div>
            </div>
          </div>
          
          <div className="ml-2">
            {isExpanded ? 
              <ChevronUp className={`w-5 h-5 ${colors.text}`} /> : 
              <ChevronDown className={`w-5 h-5 ${colors.text}`} />
            }
          </div>
        </div>
        
        <div className="w-full h-1 bg-gray-200">
          <div 
            className={`h-1 bg-gradient-to-r ${colors.gradient}`}
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
      </div>
      
      {isExpanded && objectives.length > 0 && (
        <div className="p-4 bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {objectives.map((objective) => (
              <ObjectiveCard 
                key={objective.id}
                objective={objective}
                onClick={handleObjectiveClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Composant principal du Dashboard
const Dashboard = ({ userId }) => {
  const navigate = useNavigate();
  
  const [viewMode, setViewMode] = useState('domains');
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    domain: 'all',
    type: 'all',
    period: 'month',
    showRecent: false
  });

  // États pour les données Firebase
  const [competences, setCompetences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState({});

  // Types d'objectifs
  const objectiveTypes = [
    { id: 'all', label: 'Tous les types' },
    { id: 'project', label: 'Projet' },
    { id: 'idea', label: 'Idée' },
    { id: 'desire', label: 'Désir' },
    { id: 'skill', label: 'Compétence' }
  ];

  // Effet pour charger les compétences depuis Firebase
  useEffect(() => {
    const fetchCompetences = async () => {
      if (!userId) {
        setError("Utilisateur non défini.");
        setLoading(false);
        return;
      }

      try {
        const competencesCollection = collection(db, 'users', userId, 'competences');
        const competencesSnapshot = await getDocs(competencesCollection);
        const competencesList = competencesSnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Calculer le vrai nombre d'actions et leur état depuis les données
          let realActionsCount = 0;
          let completedActionsCount = 0;
          
          // Si l'objectif a un plan d'action avec des steps
          if (data.actionPlan && data.actionPlan.steps && Array.isArray(data.actionPlan.steps)) {
            realActionsCount = data.actionPlan.steps.length;
            // Compter les actions terminées
            completedActionsCount = data.actionPlan.steps.filter(step => step.termine === true).length;
          }
          // Ou si les actions sont dans les recommandations
          else if (data.recommendation && data.recommendation.actions && Array.isArray(data.recommendation.actions)) {
            realActionsCount = data.recommendation.actions.length;
            // Si les recommandations ont aussi un statut de completion
            completedActionsCount = data.recommendation.actions.filter(action => action.completed === true).length;
          }
          // Valeur par défaut si aucune action trouvée
          else {
            realActionsCount = 0;
            completedActionsCount = 0;
          }

          // Calculer le pourcentage de progression basé sur les actions terminées
          const actionProgress = realActionsCount > 0 ? Math.round((completedActionsCount / realActionsCount) * 100) : 0;

          // Calculer le vrai nombre de concepts clés
          let realKeyConceptsCount = 0;
          if (data.keyConcepts && Array.isArray(data.keyConcepts)) {
            realKeyConceptsCount = data.keyConcepts.length;
          } else if (data.recommendation && data.recommendation.keyConcepts && Array.isArray(data.recommendation.keyConcepts)) {
            realKeyConceptsCount = data.recommendation.keyConcepts.length;
          }
          
          // Déterminer la phase en fonction des données disponibles
          let phase = 'ambition';
          let phaseProgress = 0;
          
          if (data.recommendation) {
            phase = 'stratégie';
            phaseProgress = 20; // 20% si des recommandations sont générées
          }
          
          if (data.actionPlan || data.planExists) {
            phase = 'action';
            phaseProgress = 40; // 40% si un plan d'action est généré
          }
          
          return {
            id: doc.id,
            ...data,
            phase: phase,
            phaseProgress: phaseProgress,
            globalProgress: data.globalProgress || actionProgress || phaseProgress,
            shortTitle: data.shortTitle || data.title || '',
            name: data.shortTitle || data.title || '',
            type: data.type || 'project',
            domain: data.chapitre || 'Non classé',
            chapitre: data.chapitre || 'Non classé',
            // Utiliser les vraies données au lieu de valeurs aléatoires
            keyConceptsCount: realKeyConceptsCount,
            actionsCount: realActionsCount,
            completedActionsCount: completedActionsCount,
            actionProgress: actionProgress,
            performance: data.globalProgress || actionProgress || phaseProgress,
            createdAt: data.createdAt || data.timestamp || new Date()
          };
        });
        
        setCompetences(competencesList);

        const userProgress = await loadUserProgress(userId);
        setProgressData(userProgress);
        
        // Initialisation des sections développées
        const domains = [...new Set(competencesList.map(comp => comp.chapitre).filter(Boolean))];
        const initialExpanded = {};
        domains.forEach(domain => {
          initialExpanded[domain] = false; // Sections fermées par défaut
        });
        setExpandedSections(initialExpanded);
        
      } catch (error) {
        setError("Erreur lors de la récupération des compétences : " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetences();
  }, [userId]);

  // Fonction pour obtenir les projets récents (7 derniers jours)
  const getRecentObjectives = (objectives) => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return objectives.filter(obj => {
      const createdDate = obj.createdAt ? new Date(obj.createdAt.seconds ? obj.createdAt.seconds * 1000 : obj.createdAt) : new Date();
      return createdDate >= sevenDaysAgo;
    });
  };

  // Calcul des KPIs selon la vue sélectionnée
  const getFilteredObjectives = () => {
    let objectives = competences;

    // Filtres additionnels
    objectives = objectives.filter(obj => {
      const matchesSearch = filters.search === '' || 
        obj.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        obj.domain.toLowerCase().includes(filters.search.toLowerCase());
      
      const matchesDomain = filters.domain === 'all' || obj.domain === filters.domain;
      const matchesType = filters.type === 'all' || obj.type === filters.type;
      
      // Filtrage par KPI sélectionné
      if (selectedKPI === 'strategy-phase') {
        return obj.phase === 'stratégie' && matchesSearch && matchesDomain && matchesType;
      }
      if (selectedKPI === 'transformation-phase') {
        return obj.phase === 'action' && matchesSearch && matchesDomain && matchesType;
      }
      if (selectedKPI === 'high-performance') {
        return obj.performance >= 75 && matchesSearch && matchesDomain && matchesType;
      }
      
      return matchesSearch && matchesDomain && matchesType;
    });

    // Filtrage par projets récents si activé
    if (filters.showRecent) {
      objectives = getRecentObjectives(objectives);
    }

    return objectives;
  };

  const filteredObjectives = getFilteredObjectives();

  // Récupérer tous les domaines disponibles
  const getAvailableDomains = () => {
    return [...new Set(competences.map(comp => comp.chapitre).filter(Boolean))];
  };

  const availableDomains = getAvailableDomains();

  // Calcul des KPIs
  const calculateKPIs = () => {
    const total = competences.length; // Utiliser toutes les compétences pour les pourcentages
    const totalFiltered = filteredObjectives.length;
    const completed = filteredObjectives.filter(obj => obj.performance >= 75).length;
    
    // Calcul des pourcentages par phase (sur toutes les compétences)
    const strategyPhase = competences.filter(obj => obj.phase === 'stratégie').length;
    const transformationPhase = competences.filter(obj => obj.phase === 'action').length;
    
    const strategyPercentage = total > 0 ? Math.round((strategyPhase / total) * 100) : 0;
    const transformationPercentage = total > 0 ? Math.round((transformationPhase / total) * 100) : 0;
    
    const totalConcepts = filteredObjectives.reduce((sum, obj) => sum + (obj.keyConceptsCount || 0), 0);
    const totalActions = filteredObjectives.reduce((sum, obj) => sum + (obj.actionsCount || 0), 0);
    const totalCompletedActions = filteredObjectives.reduce((sum, obj) => sum + (obj.completedActionsCount || 0), 0);

    // Compter les projets récents
    const recentObjectives = getRecentObjectives(competences);

    return {
      strategyPercentage: `${strategyPercentage}%`,
      transformationPercentage: `${transformationPercentage}%`,
      masteredObjectives: `${completed}/${totalFiltered}`,
      concepts: totalConcepts,
      actions: `${totalCompletedActions}/${totalActions}`,
      recentCount: recentObjectives.length
    };
  };

  const kpis = calculateKPIs();

  // Groupement par domaine
  const groupByDomain = () => {
    return filteredObjectives.reduce((domains, objective) => {
      const domain = objective.domain || 'Non classé';
      if (!domains[domain]) domains[domain] = [];
      domains[domain].push(objective);
      return domains;
    }, {});
  };

  const groupedData = groupByDomain();

  // Gestion des interactions
  const handleObjectiveClick = (objective) => {
    if (objective.phase === 'action') {
      navigate(`/actionPlan/${objective.id}`, { 
        state: { competence: objective, objective: objective } 
      });
    } else if (objective.phase === 'stratégie') {
      navigate(`/Recommandations/${objective.id}`, { 
        state: { competence: objective, objective: objective }
      });
    } else {
      navigate(`/formulation-objectif`, { 
        state: { editObjective: objective }
      });
    }
  };

  const toggleSection = (domain) => {
    setExpandedSections(prev => ({
      ...prev,
      [domain]: !prev[domain]
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSelectedKPI(null);
  };

  const exportToCSV = () => {
    let csvContent = "Objectif,Domaine,Type,Performance,Concepts clés,Actions terminées,Actions totales\n";
    
    filteredObjectives.forEach(obj => {
      csvContent += `"${obj.name}","${obj.domain}","${obj.type}","${obj.performance}%","${obj.keyConceptsCount}","${obj.completedActionsCount}","${obj.actionsCount}"\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `objectifs-${filters.showRecent ? 'recents-' : ''}${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fonction pour générer le titre dynamique
  const getDynamicTitle = () => {
    if (selectedKPI === 'high-performance') {
      return 'Objectifs réussis (≥75%)';
    }
    if (selectedKPI === 'strategy-phase') {
      return 'Projets en phase stratégie';
    }
    if (selectedKPI === 'transformation-phase') {
      return 'Projets en transformation';
    }
    if (filters.showRecent) {
      return 'Projets récents (7 derniers jours)';
    }
    if (filters.domain !== 'all') {
      return `Objectifs • ${filters.domain}`;
    }
    return 'Vos objectifs';
  };

  // Gestion de l'état de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-lg text-blue-600">Chargement de votre tableau de bord...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 m-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* En-tête avec contrôles intégrés */}
      <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-b-xl shadow-lg border-b border-gray-200 p-6 mb-8">
        <div className="max-w-[2000px] mx-auto space-y-6">
          {/* Titre principal */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Target className="w-6 h-6 text-indigo-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Tableau de bord
                </h1>
                <p className="text-gray-600">Vue d'ensemble de vos objectifs</p>
              </div>
            </div>
            {/* Compteur d'objectifs repositionné dans le header */}
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
              <Eye className="w-4 h-4" />
              <span className="font-medium">{filteredObjectives.length} objectif{filteredObjectives.length > 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* Contrôles de recherche et filtrage intégrés */}
          <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Rechercher un objectif..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              
              <div className="flex flex-wrap gap-3">
                <select
                  value={filters.domain}
                  onChange={(e) => handleFilterChange('domain', e.target.value)}
                  className="rounded-lg border border-gray-200 px-4 py-2.5 bg-white/90 backdrop-blur-sm text-gray-600 focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <option value="all">Tous les domaines</option>
                  {availableDomains.map(domain => (
                    <option key={domain} value={domain}>{domain}</option>
                  ))}
                </select>

                <button
                  onClick={() => handleFilterChange('showRecent', !filters.showRecent)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all ${
                    filters.showRecent 
                      ? 'bg-purple-100 text-purple-700 border border-purple-200 shadow-sm' 
                      : 'bg-white/90 backdrop-blur-sm text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">Récents</span>
                </button>

                <button
                  onClick={() => navigate('/formulation-objectif')}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span className="font-medium">Nouvel objectif</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[2000px] mx-auto px-4 py-1 space-y-6">
        
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            icon={Compass}
            title="Projets en stratégie"
            value={kpis.strategyPercentage}
            change={3}
            color="#3498db"
            onClick={() => setSelectedKPI(selectedKPI === 'strategy-phase' ? null : 'strategy-phase')}
            isActive={selectedKPI === 'strategy-phase'}
            subtitle="Phase de planification"
          />
          
          <KPICard
            icon={Cog}
            title="Projets en transformation"
            value={kpis.transformationPercentage}
            change={5}
            color="#2ecc71"
            onClick={() => setSelectedKPI(selectedKPI === 'transformation-phase' ? null : 'transformation-phase')}
            isActive={selectedKPI === 'transformation-phase'}
            subtitle="Plans d'action actifs"
          />
          
          <KPICard
            icon={Trophy}
            title="Objectifs atteints"
            value={kpis.masteredObjectives}
            change={4}
            color="#f1c40f"
            onClick={() => setSelectedKPI(selectedKPI === 'high-performance' ? null : 'high-performance')}
            isActive={selectedKPI === 'high-performance'}
            subtitle="≥ 75% de progression"
          />
          
          <KPICard
            icon={Calendar}
            title="Projets récents"
            value={kpis.recentCount}
            change={15}
            color="#9b59b6"
            onClick={() => handleFilterChange('showRecent', !filters.showRecent)}
            isActive={filters.showRecent}
            subtitle="7 derniers jours"
          />
        </div>

        {/* Contrôles d'affichage et export - positionnés logiquement avant la liste */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 font-medium">Affichage :</span>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('domains')}
                  className={`p-2 rounded-md transition-all ${viewMode === 'domains' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Vue par domaines"
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-2 rounded-md transition-all ${viewMode === 'cards' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Vue cartes"
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Vue liste"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-lg transition-colors"
              >
                <Download className="w-5 h-5" />
                <span className="hidden sm:inline">Exporter</span>
              </button>
            </div>
          </div>
        </div>

        {/* Contenu principal selon le mode d'affichage */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">
              {getDynamicTitle()}
            </h2>
          </div>

          {filteredObjectives.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Target className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-800">Aucun objectif trouvé</h3>
                <p className="text-gray-600 max-w-md">
                  {filters.search || filters.domain !== 'all' || filters.type !== 'all' || selectedKPI || filters.showRecent
                    ? "Aucun objectif ne correspond à vos critères. Essayez de modifier vos filtres."
                    : "Commencez par créer votre premier objectif."}
                </p>
                
                {!filters.search && filters.domain === 'all' && filters.type === 'all' && !selectedKPI && !filters.showRecent && (
                  <button
                    onClick={() => navigate('/formulation-objectif')}
                    className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Créer un objectif
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {viewMode === 'domains' ? (
                // Vue par domaines
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(groupedData).map(([domain, objectives]) => (
                    <DomainCard
                      key={domain}
                      domain={domain}
                      objectives={objectives}
                      isExpanded={expandedSections[domain] || false}
                      toggleExpand={toggleSection}
                      handleObjectiveClick={handleObjectiveClick}
                    />
                  ))}
                </div>
              ) : viewMode === 'cards' ? (
                // Vue cartes simple
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredObjectives.map(objective => (
                    <ObjectiveCard 
                      key={objective.id}
                      objective={objective}
                      onClick={handleObjectiveClick}
                    />
                  ))}
                </div>
              ) : (
                // Vue liste adaptée mobile
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* En-tête pour desktop */}
                  <div className="bg-gray-50 border-b border-gray-200 hidden md:block">
                    <div className="grid grid-cols-12 gap-4 p-4 text-sm font-medium text-gray-600">
                      <div className="col-span-4">Objectif</div>
                      <div className="col-span-2">Domaine</div>
                      <div className="col-span-1">Type</div>
                      <div className="col-span-2">Progression</div>
                      <div className="col-span-1">Phase</div>
                      <div className="col-span-2">Actions</div>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {filteredObjectives.map((objective) => (
                      <div key={objective.id} 
                           className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                           onClick={() => handleObjectiveClick(objective)}>
                        
                        {/* Vue desktop */}
                        <div className="hidden md:grid grid-cols-12 gap-4 p-4">
                          <div className="col-span-4 flex items-center gap-2">
                            {objective.performance >= 75 ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : objective.performance >= 50 ? (
                              <Clock className="w-4 h-4 text-yellow-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                            <span className="font-medium text-gray-800 truncate">{objective.name}</span>
                          </div>
                          
                          <div className="col-span-2 text-sm text-gray-600 truncate">
                            {objective.domain}
                          </div>
                          
                          <div className="col-span-1">
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                              {objective.type}
                            </span>
                          </div>
                          
                          <div className="col-span-2 flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-2 rounded-full transition-all duration-500"
                                style={{ 
                                  width: `${objective.performance}%`,
                                  backgroundColor: objective.performance >= 75 ? '#22c55e' : 
                                                 objective.performance >= 50 ? '#3498db' : 
                                                 objective.performance >= 25 ? '#f59e0b' : '#ef4444'
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium">{objective.performance}%</span>
                          </div>
                          
                          <div className="col-span-1">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              objective.phase === 'action' ? 'bg-green-100 text-green-700' :
                              objective.phase === 'stratégie' ? 'bg-blue-100 text-blue-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {objective.phase}
                            </span>
                          </div>
                          
                          <div className="col-span-2 flex gap-1">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/Recommandations/${objective.id}`, { 
                                  state: { competence: objective, objective: objective }
                                });
                              }}
                              className="text-xs px-2 py-1 bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-100"
                            >
                              Stratégie
                            </button>
                            {objective.phase !== 'ambition' && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/actionPlan/${objective.id}`, { 
                                    state: { competence: objective, objective: objective }
                                  });
                                }}
                                className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100"
                              >
                                Plan
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Vue mobile - format carte compacte */}
                        <div className="md:hidden p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              {objective.performance >= 75 ? (
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              ) : objective.performance >= 50 ? (
                                <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              )}
                              <h3 className="font-medium text-gray-800 text-sm line-clamp-2">{objective.name}</h3>
                            </div>
                            <div className="ml-2 text-right flex-shrink-0">
                              <span className="text-sm font-medium text-gray-700">{objective.performance}%</span>
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-center text-xs text-gray-500">
                            <span className="truncate">{objective.domain}</span>
                            <span className={`px-2 py-1 rounded-full ml-2 ${
                              objective.phase === 'action' ? 'bg-green-100 text-green-700' :
                              objective.phase === 'stratégie' ? 'bg-blue-100 text-blue-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {objective.phase}
                            </span>
                          </div>
                          
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-2 rounded-full transition-all duration-500"
                              style={{ 
                                width: `${objective.performance}%`,
                                backgroundColor: objective.performance >= 75 ? '#22c55e' : 
                                               objective.performance >= 50 ? '#3498db' : 
                                               objective.performance >= 25 ? '#f59e0b' : '#ef4444'
                              }}
                            />
                          </div>
                          
                          <div className="flex gap-2 flex-wrap">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/Recommandations/${objective.id}`, { 
                                  state: { competence: objective, objective: objective }
                                });
                              }}
                              className="text-xs px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full hover:bg-indigo-100 flex items-center gap-1"
                            >
                              <Map className="w-3 h-3" />
                              Stratégie
                            </button>
                            {objective.phase !== 'ambition' && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/actionPlan/${objective.id}`, { 
                                    state: { competence: objective, objective: objective }
                                  });
                                }}
                                className="text-xs px-3 py-1 bg-green-50 text-green-600 rounded-full hover:bg-green-100 flex items-center gap-1"
                              >
                                <Cog className="w-3 h-3" />
                                Plan
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Insights */}
        {filteredObjectives.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-6">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-2">Vue d'ensemble</h3>
                  <div className="text-sm text-gray-600 space-y-2">
                    <p>• {competences.filter(o => o.phase === 'stratégie').length} projets en phase stratégie</p>
                    <p>• {competences.filter(o => o.phase === 'action').length} projets en transformation</p>
                    <p>• {filteredObjectives.reduce((sum, o) => sum + (o.completedActionsCount || 0), 0)} actions terminées sur {filteredObjectives.reduce((sum, o) => sum + (o.actionsCount || 0), 0)}</p>
                    {filters.showRecent && <p>• {kpis.recentCount} nouveaux projets cette semaine</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <Lightbulb className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-2">Actions recommandées</h3>
                  <div className="text-sm text-gray-600 space-y-2">
                    <p>• Commencez par les objectifs les plus en retard</p>
                    <p>• Développez vos stratégies avant de passer à l'action</p>
                    <p>• Suivez régulièrement l'avancement de vos transformations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;